import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  HardHat,
  Calendar,
  Clock,
  AlertTriangle,
  Check,
  X,
  Users,
  ArrowLeft,
  Loader2,
  UserX,
  Briefcase,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { EquipeIcon } from '@/components/equipe/EquipeIcon';
import { useToast } from '@/hooks/use-toast';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { calculerJoursOuvres } from '@/lib/heures-utils';
import { supabase } from '@/integrations/supabase/client';
import { useAffectationChantierBulk } from '@/hooks/useAffectationChantierBulk';
import {
  BulkAbsenceConflict,
  BulkAffectationConflict,
  getTypeAbsenceLabel,
} from '@/types/conflits-absence';
import WeekendConfirmDialog, { isWeekend } from '@/components/ui/WeekendConfirmDialog';
import { cn } from '@/lib/utils';
import { useModalProtection } from '@/hooks/useModalProtection';

// ============================================================
// TYPES
// ============================================================

interface Membre {
  id: string;
  prenom: string;
  nom: string;
  poste?: string;
  taux_horaire?: number;
  equipe_id?: string;
  statut?: 'disponible' | 'sur_chantier' | 'en_conges' | 'arret_maladie' | 'formation' | 'repos';
  // Affectation actuelle (si sur_chantier)
  affectation_actuelle?: {
    chantier_nom: string;
    date_fin: string;
  };
}

interface Equipe {
  id: string;
  nom: string;
  couleur?: string;
  pictogramme?: string;
}

interface Chantier {
  id: string;
  nom_chantier: string;
}

interface ModalAffectationChantierBulkProps {
  isOpen: boolean;
  onClose: () => void;
  membres: Membre[];
  entrepriseId: string;
  onSuccess: () => void;
  // Props optionnelles pour usage depuis un chantier (pré-sélection)
  preSelectedChantierId?: string;
  preSelectedChantierNom?: string;
  // Props optionnelles pour pré-remplir les dates (usage depuis Planning)
  preSelectedDateDebut?: string;
  preSelectedDateFin?: string;
  // Liste des équipes pour le groupement (optionnel)
  equipes?: Equipe[];
}

// Statut d'un membre après vérification
interface MembreVerificationStatus {
  membreId: string;
  isDisponible: boolean;
  hasAbsence: boolean;
  hasAffectation: boolean;
  totalHeuresExistantes: number;
  absences: BulkAbsenceConflict[];
  affectations: BulkAffectationConflict[];
}

// ============================================================
// COMPOSANT
// ============================================================

export function ModalAffectationChantierBulk({
  isOpen,
  onClose,
  membres,
  entrepriseId,
  onSuccess,
  preSelectedChantierId,
  preSelectedChantierNom,
  preSelectedDateDebut,
  preSelectedDateFin,
  equipes = [],
}: ModalAffectationChantierBulkProps) {
  const { toast } = useToast();
  useModalProtection(isOpen);

  // Indique si le chantier est pré-sélectionné (usage depuis page chantier)
  const isChantierPreSelected = !!preSelectedChantierId;

  // État pour les équipes dépliées/repliées
  const [expandedEquipes, setExpandedEquipes] = useState<Set<string>>(new Set());

  // Grouper les membres par équipe
  const membresParEquipe = useMemo(() => {
    const grouped = new Map<string, { equipe: Equipe | null; membres: Membre[] }>();

    // Groupe "Sans équipe"
    grouped.set('sans-equipe', { equipe: null, membres: [] });

    // Créer un groupe pour chaque équipe
    equipes.forEach((eq) => {
      grouped.set(eq.id, { equipe: eq, membres: [] });
    });

    // Répartir les membres dans leurs groupes
    membres.forEach((membre) => {
      const groupKey = membre.equipe_id || 'sans-equipe';
      if (grouped.has(groupKey)) {
        grouped.get(groupKey)!.membres.push(membre);
      } else {
        // Équipe non trouvée, mettre dans "sans équipe"
        grouped.get('sans-equipe')!.membres.push(membre);
      }
    });

    // Filtrer les groupes vides et convertir en array
    return Array.from(grouped.entries())
      .filter(([, data]) => data.membres.length > 0)
      .sort((a, b) => {
        // "Sans équipe" en dernier
        if (a[0] === 'sans-equipe') return 1;
        if (b[0] === 'sans-equipe') return -1;
        // Sinon tri alphabétique
        return (a[1].equipe?.nom || '').localeCompare(b[1].equipe?.nom || '');
      });
  }, [membres, equipes]);

  // Obtenir le label et la couleur du statut (couleurs vives pour meilleure visibilité)
  const getStatutInfo = (statut?: string) => {
    switch (statut) {
      case 'sur_chantier':
        return { label: 'Sur chantier', emoji: '🏗️', className: 'bg-orange-500 text-white' };
      case 'disponible':
        return { label: 'Disponible', emoji: '✅', className: 'bg-green-600 text-white' };
      case 'repos':
        return { label: 'Repos', emoji: '🏠', className: 'bg-blue-500 text-white' };
      case 'en_conges':
        return { label: 'En congés', emoji: '🏖️', className: 'bg-purple-500 text-white' };
      case 'arret_maladie':
        return { label: 'En arrêt', emoji: '🏥', className: 'bg-red-500 text-white' };
      case 'formation':
        return { label: 'Formation', emoji: '📚', className: 'bg-indigo-500 text-white' };
      default:
        return { label: 'Disponible', emoji: '✅', className: 'bg-green-600 text-white' };
    }
  };

  // Toggle une équipe ouverte/fermée
  const toggleEquipe = (equipeId: string) => {
    setExpandedEquipes((prev) => {
      const next = new Set(prev);
      if (next.has(equipeId)) {
        next.delete(equipeId);
      } else {
        next.add(equipeId);
      }
      return next;
    });
  };

  // Sélectionner/désélectionner tous les membres d'une équipe
  const toggleEquipeMembres = (equipeId: string, membres: Membre[]) => {
    const membreIds = membres.map((m) => m.id);
    const allSelected = membreIds.every((id) => membresCoches.has(id));

    setMembresCoches((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        // Désélectionner tous
        membreIds.forEach((id) => next.delete(id));
      } else {
        // Sélectionner tous
        membreIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };
  // Hook bulk
  const {
    checkDisponibiliteBulk,
    getConflitsDetails,
    affecterMembresAuChantier,
    isLoading: isCreating,
  } = useAffectationChantierBulk();

  // États du formulaire
  const [chantierId, setChantierId] = useState('');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [heuresParJour, setHeuresParJour] = useState(8);
  const [joursOuvres, setJoursOuvres] = useState(0);

  // États de l'étape actuelle
  // Si chantier pré-sélectionné, on commence par l'étape de sélection des membres
  const [etape, setEtape] = useState<'selection' | 'formulaire' | 'recapitulatif'>('formulaire');

  // États des chantiers disponibles
  const [chantiers, setChantiers] = useState<Chantier[]>([]);
  const [isLoadingChantiers, setIsLoadingChantiers] = useState(false);

  // États de la vérification
  const [isChecking, setIsChecking] = useState(false);
  const [verificationStatuses, setVerificationStatuses] = useState<Map<string, MembreVerificationStatus>>(new Map());
  const [membresCoches, setMembresCoches] = useState<Set<string>>(new Set());
  // IDs des membres envoyés à la vérification (pour filtrer le récapitulatif)
  const [membresVerifiesIds, setMembresVerifiesIds] = useState<Set<string>>(new Set());

  // État pour le popup de confirmation weekend
  const [showWeekendDialog, setShowWeekendDialog] = useState(false);
  const [pendingDateFin, setPendingDateFin] = useState<string>('');
  const [inclureWeekendFin, setInclureWeekendFin] = useState(false);

  // Réinitialiser le formulaire à l'ouverture
  useEffect(() => {
    if (isOpen) {
      const today = new Date().toISOString().split('T')[0];
      // Si chantier pré-sélectionné, utiliser son ID, sinon vide
      setChantierId(preSelectedChantierId || '');
      // Utiliser les dates pré-sélectionnées si fournies, sinon aujourd'hui
      setDateDebut(preSelectedDateDebut || today);
      setDateFin(preSelectedDateFin || today);
      setHeuresParJour(8);
      setVerificationStatuses(new Map());
      setMembresVerifiesIds(new Set());
      setInclureWeekendFin(false);
      // Replier toutes les équipes par défaut pour une meilleure lisibilité
      setExpandedEquipes(new Set());

      // Si on vient du chantier (pré-sélectionné) : étape sélection + aucun membre coché
      // Si on vient de la page Équipe ou Planning : étape formulaire + tous les membres déjà cochés
      if (preSelectedChantierId) {
        setEtape('selection');
        setMembresCoches(new Set()); // Aucun membre pré-coché
      } else {
        setEtape('formulaire');
        // Pré-cocher tous les membres au moment de l'ouverture
        setMembresCoches(new Set(membres.map((m) => m.id)));
        loadChantiers();
      }
    }
    // Ne PAS inclure `membres` dans les deps pour éviter un reset de la sélection
    // quand le parent re-render avec un nouvel array (même contenu)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, preSelectedChantierId, preSelectedDateDebut, preSelectedDateFin]);

  // Calcul automatique des jours ouvrés
  useEffect(() => {
    if (dateDebut && dateFin && dateDebut <= dateFin) {
      const jours = calculerJoursOuvres(dateDebut, dateFin, inclureWeekendFin);
      setJoursOuvres(jours);
    } else {
      setJoursOuvres(0);
    }
  }, [dateDebut, dateFin, inclureWeekendFin]);

  // Charger les chantiers disponibles
  const loadChantiers = async () => {
    if (!entrepriseId) return;

    setIsLoadingChantiers(true);
    try {
      const { data, error } = await supabase
        .from('chantiers')
        .select('id, nom_chantier')
        .eq('entreprise_id', entrepriseId)
        .in('etat_chantier', ['planifie', 'en_cours'])
        .order('nom_chantier');

      if (error) throw error;
      setChantiers(data || []);
    } catch (err) {
      console.error('Erreur chargement chantiers:', err);
    } finally {
      setIsLoadingChantiers(false);
    }
  };

  // Gérer le changement de date de fin avec vérification weekend
  const handleDateFinChange = (newDateFin: string) => {
    if (!newDateFin) {
      setDateFin('');
      return;
    }
    const date = parseISO(newDateFin);
    if (isWeekend(date)) {
      setPendingDateFin(newDateFin);
      setShowWeekendDialog(true);
    } else {
      setDateFin(newDateFin);
      setInclureWeekendFin(false);
    }
  };

  // Callback du dialogue weekend
  const handleWeekendConfirm = (inclure: boolean) => {
    setInclureWeekendFin(inclure);
    setDateFin(pendingDateFin);
    setShowWeekendDialog(false);
    setPendingDateFin('');
  };

  // Vérification formulaire valide
  const isFormValid = useMemo(() => {
    // Le chantierId doit être soit pré-sélectionné, soit choisi dans le select
    const hasChantier = isChantierPreSelected ? !!preSelectedChantierId : chantierId !== '';
    return (
      hasChantier &&
      dateDebut !== '' &&
      dateFin !== '' &&
      dateDebut <= dateFin &&
      heuresParJour > 0 &&
      heuresParJour <= 16 &&
      membres.length > 0
    );
  }, [chantierId, dateDebut, dateFin, heuresParJour, membres, isChantierPreSelected, preSelectedChantierId]);

  // Nom du chantier sélectionné
  const selectedChantierNom = useMemo(() => {
    // Si pré-sélectionné, utiliser le nom fourni
    if (preSelectedChantierNom) return preSelectedChantierNom;
    // Sinon chercher dans la liste des chantiers chargés
    const chantier = chantiers.find((c) => c.id === chantierId);
    return chantier?.nom_chantier || null;
  }, [chantiers, chantierId, preSelectedChantierNom]);

  // Vérifier la disponibilité des membres sélectionnés
  const handleVerifierDisponibilite = async () => {
    if (!isFormValid) return;

    setIsChecking(true);
    try {
      // Ne vérifier que les membres cochés par l'utilisateur
      const membreIds = Array.from(membresCoches);

      // Mémoriser quels membres ont été vérifiés (pour le récapitulatif)
      setMembresVerifiesIds(new Set(membresCoches));

      // 1. Vérifier la disponibilité globale
      const disponibilites = await checkDisponibiliteBulk(membreIds, dateDebut, dateFin);

      // 2. Récupérer les détails des conflits
      const conflits = await getConflitsDetails(membreIds, dateDebut, dateFin);

      // 3. Construire le statut par membre (uniquement les cochés)
      const statuses = new Map<string, MembreVerificationStatus>();

      for (const membre of membres.filter(m => membresCoches.has(m.id))) {
        const dispo = disponibilites.disponibilites.find((d) => d.membre_id === membre.id);
        const absences = conflits.absences.filter((a) => a.membre_id === membre.id);
        const affectations = conflits.affectations.filter((a) => a.membre_id === membre.id);

        // Calculer si le total heures dépasse 16h avec la nouvelle affectation
        const totalHeuresExistantes = dispo?.total_heures_jour || 0;
        const totalApreAffectation = totalHeuresExistantes + heuresParJour;
        const depasseHeures = totalApreAffectation > 16;

        statuses.set(membre.id, {
          membreId: membre.id,
          isDisponible: (dispo?.est_disponible ?? true) && !depasseHeures,
          hasAbsence: absences.length > 0,
          hasAffectation: affectations.length > 0,
          totalHeuresExistantes,
          absences,
          affectations,
        });
      }

      setVerificationStatuses(statuses);

      // Passer à l'étape récapitulatif (membresCoches reste inchangé)
      setEtape('recapitulatif');
    } catch (err) {
      console.error('Erreur vérification:', err);
    } finally {
      setIsChecking(false);
    }
  };

  // Toggle cocher/décocher un membre
  const handleToggleMembre = (membreId: string) => {
    const newSet = new Set(membresCoches);
    if (newSet.has(membreId)) {
      newSet.delete(membreId);
    } else {
      newSet.add(membreId);
    }
    setMembresCoches(newSet);
  };

  // Toggle cocher/décocher tous
  const handleToggleAll = () => {
    if (membresCoches.size === membres.length) {
      setMembresCoches(new Set());
    } else {
      setMembresCoches(new Set(membres.map((m) => m.id)));
    }
  };

  // Créer les affectations
  const handleConfirmer = async () => {
    const membreIdsAAffecter = Array.from(membresCoches);
    if (membreIdsAAffecter.length === 0) return;

    // Utiliser le chantierId pré-sélectionné si fourni, sinon celui du select
    const targetChantierId = preSelectedChantierId || chantierId;

    const result = await affecterMembresAuChantier({
      membreIds: membreIdsAAffecter,
      chantierId: targetChantierId,
      dateDebut,
      dateFin,
      heuresParJour,
    });

    if (result.success) {
      toast({
        title: `${result.created} membre(s) affecté(s)`,
        description: result.failed > 0
          ? `${result.failed} échec(s)`
          : "Affectation réussie",
      });
      onSuccess();
      onClose();
    } else {
      toast({
        title: "Erreur lors de l'affectation",
        description: result.errors?.[0]?.error || "Une erreur est survenue",
        variant: "destructive",
      });
    }
  };

  // Compter les membres avec/sans conflits
  const stats = useMemo(() => {
    let disponibles = 0;
    let avecConflits = 0;

    verificationStatuses.forEach((status) => {
      if (status.isDisponible && !status.hasAbsence && !status.hasAffectation) {
        disponibles++;
      } else {
        avecConflits++;
      }
    });

    return { disponibles, avecConflits, coches: membresCoches.size };
  }, [verificationStatuses, membresCoches]);

  // Déterminer le statut visuel d'un membre
  const getMembreStatut = (status: MembreVerificationStatus | undefined) => {
    if (!status) return { type: 'ok' as const, label: 'OK', className: 'bg-green-500/20 text-green-700' };

    if (status.hasAbsence) {
      return { type: 'absence' as const, label: 'Absence', className: 'bg-orange-500/20 text-orange-700' };
    }

    const totalApres = status.totalHeuresExistantes + heuresParJour;
    if (totalApres > 16) {
      return { type: 'heures' as const, label: `>${16}h/j`, className: 'bg-red-500/20 text-red-700' };
    }

    if (status.hasAffectation) {
      return { type: 'affectation' as const, label: `${status.totalHeuresExistantes}h existantes`, className: 'bg-blue-500/20 text-blue-700' };
    }

    return { type: 'ok' as const, label: 'Disponible', className: 'bg-green-500/20 text-green-700' };
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto !z-[200]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-primary">
            <Users className="h-5 w-5" />
            {etape === 'selection'
              ? `Affecter des membres à ${preSelectedChantierNom || 'un chantier'}`
              : `Affecter ${membresCoches.size} membre${membresCoches.size > 1 ? 's' : ''} à un chantier`}
          </DialogTitle>
          <DialogDescription>
            {etape === 'selection'
              ? 'Sélectionnez les membres à affecter à ce chantier'
              : etape === 'formulaire'
              ? 'Définissez la période d\'affectation'
              : `Récapitulatif - ${stats.coches} membre${stats.coches > 1 ? 's' : ''} sélectionné${stats.coches > 1 ? 's' : ''}`}
          </DialogDescription>
        </DialogHeader>

        {/* ============================================================ */}
        {/* ÉTAPE 0 : SÉLECTION DES MEMBRES (uniquement depuis un chantier) */}
        {/* ============================================================ */}
        {etape === 'selection' && (
          <div className="space-y-4 py-4">
            {/* Chantier concerné */}
            <div className="flex items-center gap-2 p-3 rounded-lg border bg-muted/50">
              <HardHat className="h-4 w-4 text-primary" />
              <span className="font-medium">{preSelectedChantierNom}</span>
            </div>

            {/* Sélection rapide */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                {membresCoches.size} membre{membresCoches.size > 1 ? 's' : ''} sélectionné{membresCoches.size > 1 ? 's' : ''}
              </span>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setMembresCoches(new Set(membres.map((m) => m.id)))}
                >
                  Tout sélectionner
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setMembresCoches(new Set())}
                >
                  Tout désélectionner
                </Button>
              </div>
            </div>

            {/* Liste des membres groupés par équipe */}
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {membresParEquipe.map(([equipeId, { equipe, membres: equipeMembres }]) => {
                const isExpanded = expandedEquipes.has(equipeId);
                const membresIds = equipeMembres.map((m) => m.id);
                const selectedCount = membresIds.filter((id) => membresCoches.has(id)).length;
                const allSelected = selectedCount === equipeMembres.length;
                const someSelected = selectedCount > 0 && selectedCount < equipeMembres.length;

                return (
                  <Collapsible
                    key={equipeId}
                    open={isExpanded}
                    onOpenChange={() => toggleEquipe(equipeId)}
                  >
                    <div className="border rounded-lg overflow-hidden">
                      {/* En-tête de l'équipe */}
                      <div className="flex items-center justify-between p-2.5 bg-muted/30 hover:bg-muted/50">
                        <CollapsibleTrigger asChild>
                          <div className="flex items-center gap-2 cursor-pointer flex-1">
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                            {equipe?.pictogramme && equipe?.couleur ? (
                              <EquipeIcon
                                pictogramme={equipe.pictogramme}
                                couleur={equipe.couleur}
                                size="sm"
                                showBackground={true}
                              />
                            ) : (
                              <Users className="h-4 w-4 text-muted-foreground" />
                            )}
                            <span className="font-medium text-sm">
                              {equipe?.nom || 'Sans équipe'}
                            </span>
                            <Badge variant="secondary" className="text-xs">
                              {selectedCount}/{equipeMembres.length}
                            </Badge>
                          </div>
                        </CollapsibleTrigger>
                        <Checkbox
                          checked={allSelected}
                          ref={(el) => {
                            if (el && someSelected) {
                              (el as any).indeterminate = true;
                            }
                          }}
                          onCheckedChange={() => toggleEquipeMembres(equipeId, equipeMembres)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>

                      {/* Membres de l'équipe */}
                      <CollapsibleContent>
                        <div className="divide-y">
                          {equipeMembres.map((membre) => {
                            const isCoche = membresCoches.has(membre.id);
                            const statutInfo = getStatutInfo(membre.statut);
                            return (
                              <div
                                key={membre.id}
                                className={cn(
                                  "flex items-center justify-between p-2 pl-10 cursor-pointer hover:bg-muted/30",
                                  !isCoche && "opacity-50"
                                )}
                                onClick={() => handleToggleMembre(membre.id)}
                              >
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                  <span className="font-medium text-sm whitespace-nowrap">
                                    {membre.prenom} {membre.nom}
                                  </span>
                                  {membre.poste && (
                                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                                      {membre.poste}
                                    </span>
                                  )}
                                  {/* Statut et affectation actuelle */}
                                  <div className="flex items-center gap-1.5 ml-auto mr-2">
                                    <Badge variant="secondary" className={cn("text-[10px] py-0 px-1.5", statutInfo.className)}>
                                      {statutInfo.emoji} {statutInfo.label}
                                    </Badge>
                                    {membre.statut === 'sur_chantier' && membre.affectation_actuelle && (
                                      <Badge variant="secondary" className="text-[10px] py-0 px-1.5 bg-amber-100 text-amber-800 border border-amber-300">
                                        📍 {membre.affectation_actuelle.chantier_nom} → {format(parseISO(membre.affectation_actuelle.date_fin), 'dd/MM', { locale: fr })}
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                                <Checkbox
                                  checked={isCoche}
                                  onCheckedChange={() => handleToggleMembre(membre.id)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                            );
                          })}
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                );
              })}
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* ÉTAPE 1 : FORMULAIRE */}
        {/* ============================================================ */}
        {etape === 'formulaire' && (
          <div className="space-y-4 py-4">
            {/* Liste des membres sélectionnés */}
            <div className="space-y-2">
              <Label>Membres sélectionnés ({membresCoches.size})</Label>
              <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
                {membres.filter((m) => membresCoches.has(m.id)).map((membre) => (
                  <Badge key={membre.id} variant="secondary" className="text-xs">
                    {membre.prenom} {membre.nom}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Sélection du chantier */}
            <div className="space-y-2">
              <Label htmlFor="chantier">
                <HardHat className="h-3.5 w-3.5 inline mr-1" />
                Chantier *
              </Label>
              {isChantierPreSelected ? (
                // Chantier pré-sélectionné : affichage en lecture seule
                <div className="flex items-center gap-2 p-2.5 rounded-md border bg-muted/50">
                  <HardHat className="h-4 w-4 text-primary" />
                  <span className="font-medium">{preSelectedChantierNom}</span>
                </div>
              ) : (
                // Sélection normale du chantier
                <Select value={chantierId} onValueChange={setChantierId} disabled={isLoadingChantiers}>
                  <SelectTrigger id="chantier">
                    <SelectValue placeholder={isLoadingChantiers ? 'Chargement...' : 'Sélectionner un chantier...'} />
                  </SelectTrigger>
                  <SelectContent>
                    {chantiers.map((chantier) => (
                      <SelectItem key={chantier.id} value={chantier.id}>
                        {chantier.nom_chantier}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Période d'affectation */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="dateDebut">
                  <Calendar className="h-3.5 w-3.5 inline mr-1" />
                  Date début *
                </Label>
                <Input
                  id="dateDebut"
                  type="date"
                  value={dateDebut}
                  onChange={(e) => setDateDebut(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dateFin">
                  <Calendar className="h-3.5 w-3.5 inline mr-1" />
                  Date fin *
                </Label>
                <Input
                  id="dateFin"
                  type="date"
                  value={dateFin}
                  onChange={(e) => handleDateFinChange(e.target.value)}
                />
              </div>
            </div>

            {/* Jours ouvrés */}
            {dateDebut && dateFin && dateDebut <= dateFin && (
              <div className="flex items-center gap-2 p-3 rounded-lg border border-primary/30 bg-primary/5">
                <Briefcase className="h-4 w-4 text-primary" />
                <span className="text-sm">
                  <strong>{joursOuvres}</strong> jour{joursOuvres > 1 ? 's' : ''} travaillé{joursOuvres > 1 ? 's' : ''}
                  {selectedChantierNom && <span className="text-muted-foreground ml-1">sur {selectedChantierNom}</span>}
                </span>
              </div>
            )}

            {/* Heures par jour */}
            <div className="space-y-2">
              <Label htmlFor="heures">
                <Clock className="h-3.5 w-3.5 inline mr-1" />
                Heures par jour (pour tous) *
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="heures"
                  type="number"
                  min={0.5}
                  max={16}
                  step={0.5}
                  value={heuresParJour}
                  onChange={(e) => setHeuresParJour(Math.min(16, Math.max(0.5, parseFloat(e.target.value) || 8)))}
                  className="w-24"
                />
                <span className="text-sm text-muted-foreground">h/jour (max: 16h)</span>
              </div>
            </div>

            {/* Erreur dates */}
            {dateDebut && dateFin && dateDebut > dateFin && (
              <div className="flex items-center gap-2 p-3 rounded-lg border border-red-500 bg-red-500/10">
                <X className="h-4 w-4 text-red-500" />
                <span className="text-sm font-medium text-red-600">
                  La date de fin doit être après la date de début
                </span>
              </div>
            )}
          </div>
        )}

        {/* ============================================================ */}
        {/* ÉTAPE 2 : RÉCAPITULATIF */}
        {/* ============================================================ */}
        {etape === 'recapitulatif' && (
          <div className="space-y-4 py-4">
            {/* Résumé de l'affectation */}
            <div className="p-3 rounded-lg border bg-muted/50">
              <div className="text-sm space-y-1">
                <div><strong>Chantier :</strong> {selectedChantierNom}</div>
                <div>
                  <strong>Période :</strong>{' '}
                  {format(parseISO(dateDebut), 'dd/MM/yyyy', { locale: fr })} →{' '}
                  {format(parseISO(dateFin), 'dd/MM/yyyy', { locale: fr })} ({joursOuvres} jour{joursOuvres > 1 ? 's' : ''})
                </div>
                <div><strong>Heures/jour :</strong> {heuresParJour}h</div>
              </div>
            </div>

            {/* Warning si conflits */}
            {stats.avecConflits > 0 && (
              <div className="flex items-start gap-2 p-3 rounded-lg border border-orange-500/50 bg-orange-500/10">
                <AlertTriangle className="h-4 w-4 text-orange-500 mt-0.5" />
                <div className="text-sm text-orange-700">
                  <strong>{stats.avecConflits}</strong> membre{stats.avecConflits > 1 ? 's ont' : ' a'} un conflit potentiel
                  (absence ou heures existantes). Vous pouvez quand même les affecter.
                </div>
              </div>
            )}

            {/* Tableau des membres */}
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        checked={membresCoches.size === membresVerifiesIds.size && membresVerifiesIds.size > 0}
                        onCheckedChange={() => {
                          if (membresCoches.size === membresVerifiesIds.size) {
                            setMembresCoches(new Set());
                          } else {
                            setMembresCoches(new Set(membresVerifiesIds));
                          }
                        }}
                      />
                    </TableHead>
                    <TableHead>Membre</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Détails</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {membres.filter(m => membresVerifiesIds.has(m.id)).map((membre) => {
                    const status = verificationStatuses.get(membre.id);
                    const statutVisuel = getMembreStatut(status);
                    const isCoche = membresCoches.has(membre.id);

                    return (
                      <TableRow key={membre.id} className={!isCoche ? 'opacity-50' : ''}>
                        <TableCell>
                          <Checkbox
                            checked={isCoche}
                            onCheckedChange={() => handleToggleMembre(membre.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{membre.prenom} {membre.nom}</div>
                          {membre.poste && <div className="text-xs text-muted-foreground">{membre.poste}</div>}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className={statutVisuel.className}>
                            {statutVisuel.type === 'ok' && <Check className="h-3 w-3 mr-1" />}
                            {statutVisuel.type === 'absence' && <UserX className="h-3 w-3 mr-1" />}
                            {statutVisuel.type === 'heures' && <X className="h-3 w-3 mr-1" />}
                            {statutVisuel.type === 'affectation' && <Clock className="h-3 w-3 mr-1" />}
                            {statutVisuel.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {status?.hasAbsence && status.absences.length > 0 && (
                            <div>
                              {status.absences.map((a, i) => (
                                <div key={i}>
                                  {getTypeAbsenceLabel(a.type_absence)} du{' '}
                                  {format(parseISO(a.date_debut), 'dd/MM', { locale: fr })} au{' '}
                                  {format(parseISO(a.date_fin), 'dd/MM', { locale: fr })}
                                </div>
                              ))}
                            </div>
                          )}
                          {status?.hasAffectation && status.affectations.length > 0 && (
                            <div>
                              {status.affectations.map((a, i) => (
                                <div key={i}>
                                  {a.chantier_nom} ({a.heures_par_jour || 8}h/j)
                                </div>
                              ))}
                            </div>
                          )}
                          {statutVisuel.type === 'ok' && 'Aucun conflit'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Résumé final */}
            <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
              <span className="text-sm">
                <strong>{stats.coches}</strong> membre{stats.coches > 1 ? 's' : ''} sélectionné{stats.coches > 1 ? 's' : ''} pour l'affectation
              </span>
              {stats.coches === 0 && (
                <span className="text-sm text-red-600">Sélectionnez au moins un membre</span>
              )}
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* FOOTER */}
        {/* ============================================================ */}
        <DialogFooter className="gap-2 sm:gap-0">
          {etape === 'selection' && (
            <>
              <Button type="button" variant="outline" onClick={onClose}>
                Annuler
              </Button>
              <Button
                type="button"
                onClick={() => setEtape('formulaire')}
                disabled={membresCoches.size === 0}
              >
                Continuer ({membresCoches.size} membre{membresCoches.size > 1 ? 's' : ''})
              </Button>
            </>
          )}
          {etape === 'formulaire' && (
            <>
              {isChantierPreSelected ? (
                <Button type="button" variant="outline" onClick={() => setEtape('selection')}>
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Retour
                </Button>
              ) : (
                <Button type="button" variant="outline" onClick={onClose}>
                  Annuler
                </Button>
              )}
              <Button
                type="button"
                onClick={handleVerifierDisponibilite}
                disabled={!isFormValid || isChecking || membresCoches.size === 0}
              >
                {isChecking ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Vérification...
                  </>
                ) : (
                  'Vérifier disponibilité'
                )}
              </Button>
            </>
          )}
          {etape === 'recapitulatif' && (
            <>
              <Button type="button" variant="outline" onClick={() => setEtape('formulaire')}>
                <ArrowLeft className="h-4 w-4 mr-1" />
                Retour
              </Button>
              <Button
                type="button"
                onClick={handleConfirmer}
                disabled={membresCoches.size === 0 || isCreating}
              >
                {isCreating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Création...
                  </>
                ) : (
                  `Affecter ${membresCoches.size} membre${membresCoches.size > 1 ? 's' : ''}`
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>

      {/* Dialogue de confirmation weekend */}
      <WeekendConfirmDialog
        isOpen={showWeekendDialog}
        onClose={() => {
          setShowWeekendDialog(false);
          setPendingDateFin('');
        }}
        onConfirm={handleWeekendConfirm}
        dateFin={pendingDateFin ? parseISO(pendingDateFin) : new Date()}
        type="membre"
      />
    </Dialog>
  );
}
